import CustomButton from 'components/CustomInput/Button';
import React, { useState, useEffect } from 'react';
import LoadingIndicator from 'components/LoadingIndicator';
import TextHeader from 'components/CustomText/TextHeader';
import { Grid, Checkbox } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TextSubHeader from 'components/CustomText/TextSubHeader';
import CustomTextField from 'components/Input/CustomTextField';
import CustomDatePicker from 'components/Input/CustomDatePicker';
import CustomTable from 'components/CustomTable';
import PopupInput from 'components/Application/TeamMemberForm/PopupInput';
import GeneralPostApprovalLayout from 'components/GeneralPostApprovalLayout';
import ProtocolDeviationDetails from './ProtocolDeviationDetails';
import useUploadDocData from './useUploadDocData';
import { postApprovalPermissionChecking, produce, waitUntilFormUpdate } from 'utility/general';
import { SEvenRow } from 'style/CommonStyledComponent';
import { useDispatch, useSelector } from 'react-redux';
import useDialog from 'components/ConfirmDialog/useDialog';
import {
    SAVE_PROTOCOL_DEVIATION,
    GET_PROTOCOL_DEVIATION,
    SUBMIT_PROTOCOL_DEVIATION,
    SET_PROTOCOL_DEVIATION_REPORT,
    UP_DATA_INITIAL,
} from 'store/key';
import useQuery from 'hooks/useQuery';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { PROTOCOL_DEVIATION_REPORT } from 'utility/postApprovalLookUp';
import { useHistory } from 'react-router';
import { GENERAL_SAVE_SUCCESS, GENERAL_SUBMIT_SUCCESS, ERROR_MAX_LENGTH } from 'utility/frontendMessage';
import { getTimeWithTimeZone } from 'utility/general';

const useStyles = makeStyles((theme) => ({
    page: {
        padding: 15,
    },
    itemContent: {
        paddingTop: 20,
    },
    subItem: {
        paddingTop: 10,
    },
    textSubHeader: {
        paddingBottom: 10,
    },
    leftColumn: {
        paddingRight: 30,
    },
    rightColumn: {
        paddingLeft: 30,
    },
    italicText: {
        paddingTop: 5,
        fontStyle: 'italic',
    },
}));

const ProtocolDeviation = (props) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const showDialog = useDialog();
    let appId = useQuery('appId');
    const siteId = useQuery('siteId');
    if (appId) {
        appId = Number(appId);
    }
    const reportId = useQuery('reportId');
    const location = useLocation();
    const history = useHistory();

    const protocolDeviationReport = useSelector((state) => state.protocolDeviationReport);
    const remoteDetail = protocolDeviationReport.remoteDetail;
    const rows = protocolDeviationReport.rows;
    const isLoading = protocolDeviationReport.isLoading;
    const isNotYetStarted = protocolDeviationReport.remoteDetail?.isNotYetStarted;
    const approvalDecisionDate = protocolDeviationReport.remoteDetail?.decisionDate;
    const forceErrorDisplay = protocolDeviationReport.forceErrorDisplay;
    const status = protocolDeviationReport.remoteDetail.lastStatus;
    const hasCCOAction = protocolDeviationReport.remoteDetail.hasCCOAction;

    const signedFileId = protocolDeviationReport.remoteDetail.signedFileId;
    const signedFileName = protocolDeviationReport.remoteDetail.signedFileName;

    const isPermission = postApprovalPermissionChecking({ status, reportData: protocolDeviationReport.remoteDetail });
    let isViewOnly = !isPermission;

    const isFormCreate = !reportId;
    const docType = PROTOCOL_DEVIATION_REPORT;

    const {
        columns: uploadDocColumns,
        defaultUploadDocFormList,
        onUploadDocEditSubmit,
        handleUploadDocUpdate,
        handleUploadDocDelete,
        showUploadDocDialog,
        setShowUploadDocDialog,
    } = useUploadDocData({ protocolDeviationReport, rows });

    const [signedUpload, setSignedUpload] = useState({ fileName: '', key: null });
    const [actualStudyStartDateValid, setActualStudyStartDateValid] = useState(true);
    const [studySiteValid, setStudySiteValid] = useState(true);
    const [maxLengthValidation, setMaxLengthValidation] = useState({});

    const getProtocolDeviationDetail = () => {
        dispatch({
            type: GET_PROTOCOL_DEVIATION,
            payload: {
                docType,
                reportId,
                appId,
                isFormCreate,
                siteId,
            },
        });
    };

    useEffect(() => {
        if (signedFileId && signedFileName) {
            setSignedUpload({ fileName: signedFileName, key: signedFileId });
        } else {
            setSignedUpload({ fileName: '', key: null });
        }
    }, [signedFileId, signedFileName]);

    useEffect(() => {
        getProtocolDeviationDetail();
        // eslint-disable-next-line
    }, [reportId]);

    const handleChange = (name, value) => {
        let data = { ...remoteDetail, [name]: value };
        dispatch({
            type: UP_DATA_INITIAL,
            payload: {
                key: 'remoteDetail',
                value: data,
            },
        });
    };

    const handleFormCreate = async ({ callback }) => {
        const { pathname } = location;

        const hasChange = await waitUntilFormUpdate();
        if (hasChange) {
            return;
        }

        dispatch({
            type: SAVE_PROTOCOL_DEVIATION,
            payload: {
                reportId,
                appId,
                isFormCreate,
                signedFileId: signedUpload.key,
                siteId,
                callback: (newReportId) => {
                    showDialog({
                        description: GENERAL_SAVE_SUCCESS,
                        disableCancelButton: true,
                    });

                    if (isFormCreate) {
                        callback?.(newReportId);
                        history.push(`${pathname}?reportId=${newReportId}`);
                    } else {
                        getProtocolDeviationDetail();
                        callback?.();
                    }
                },
            },
        });
    };

    const checkSubmitFormValid = () => {
        let result = true;
        dispatch({
            type: SET_PROTOCOL_DEVIATION_REPORT,
            payload: {
                forceErrorDisplay: true,
            },
        });
        if (!actualStudyStartDateValid) result = false;
        if (!studySiteValid) result = false;
        if (protocolDeviationReport.remoteDetail?.protocolDeviationReportDetails?.length === 0) result = false;
        protocolDeviationReport.remoteDetail?.protocolDeviationReportDetails?.forEach((item, index) => {
            if (!item.occurrenceDate) result = false;
            if (!(item.type === 1 || item.type === 0 || item.type === true || item.type === false)) result = false;
            if (!item.protocolDeviationDesc) result = false;
            if (!item.protocolRequirementDesc) result = false;
            if (!(item.causeAe === 1 || item.causeAe === 0 || item.causeAe === true || item.causeAe === false))
                result = false;
            if (
                !(
                    item.increaseRisks === 1 ||
                    item.increaseRisks === 0 ||
                    item.increaseRisks === true ||
                    item.increaseRisks === false
                )
            )
                result = false;
            if (
                !(
                    item.avoidDeviation === 1 ||
                    item.avoidDeviation === 0 ||
                    item.avoidDeviation === true ||
                    item.avoidDeviation === false
                )
            )
                result = false;
            if (!item.actionRemarks) result = false;
            if (!(item.deviationCaused === 0 || item.deviationCaused === 1 || item.deviationCaused === 2))
                result = false;
            if (item.causeAe === 1 || item.causeAe) {
                if (!item.aeRemarks) result = false;
            }
            if (item.increaseRisks === 1 || item.increaseRisks) {
                if (!item.risksRemark) result = false;
            }
            if (item.deviationCaused === 2) {
                if (!item.deviationRemark) result = false;
            }
        });

        return {
            valid: result,
        };
    };

    const updateMaxLength = (key, value) => {
        setMaxLengthValidation(
            produce((draft) => {
                draft[key] = value;
            }),
        );
    };

    const checkSaveFormValid = () => {
        const hasMaxLengthError = Object.values(maxLengthValidation).includes(false);

        return {
            valid: !hasMaxLengthError,
            msg: ERROR_MAX_LENGTH,
        };
    };

    const onSubmit = async () => {
        dispatch({
            type: SUBMIT_PROTOCOL_DEVIATION,
            payload: {
                reportId,
                appId,
                isFormCreate,
                signedFileId: signedUpload.key,
                siteId,
                callback: () => {
                    showDialog({
                        description: GENERAL_SUBMIT_SUCCESS,
                        disableCancelButton: true,
                    });
                    history.push('/');
                },
            },
        });
    };

    const changeNotYetStarted = (e) => {
        const value = e.target.checked;
        if (value) {
            dispatch({
                type: SET_PROTOCOL_DEVIATION_REPORT,
                payload: {
                    remoteDetail: {
                        ...remoteDetail,
                        actualStudyStartDate: null,
                        isNotYetStarted: value,
                    },
                },
            });
        } else {
            handleChange('isNotYetStarted', value);
        }
    };

    const checkIsFormDataChanged = () => {
        if (
            protocolDeviationReport.remoteDetail.addSupplements.length ||
            protocolDeviationReport.remoteDetail.deleteSupplements.length
        ) {
            return true;
        }

        let isChanged = false;
        for (let key in protocolDeviationReport.oldData) {
            if (protocolDeviationReport.oldData[key] !== protocolDeviationReport.remoteDetail[key]) {
                isChanged = true;
                break;
            }
        }
        return isChanged;
    };

    return (
        <GeneralPostApprovalLayout
            type={docType}
            isFormCreate={isFormCreate}
            onCreate={handleFormCreate}
            onSubmit={onSubmit}
            isLoading={isLoading}
            isViewOnly={isViewOnly}
            status={status}
            reportId={reportId}
            hasCCOAction={hasCCOAction}
            isPermission={isPermission}
            signedUpload={signedUpload} // case 1: object from server, case 2: object from local upload
            onSignedDocumentUploaded={(result) => {
                // result is in format {fileName: 'test.docx', key: 112 }
                setSignedUpload(result);
            }}
            checkIsFormDataChanged={checkIsFormDataChanged}
            checkSubmitFormValid={checkSubmitFormValid}
            checkSaveFormValid={checkSaveFormValid}
            siteInfo={remoteDetail?.siteInstruction}
        >
            {showUploadDocDialog && (
                <PopupInput
                    title={'Upload Supplementary Document'}
                    height="500px"
                    width="700px"
                    alignItems={'center'}
                    defaultFormList={defaultUploadDocFormList}
                    editRow={onUploadDocEditSubmit}
                    setOpenPopup={setShowUploadDocDialog}
                    tableList={rows}
                    onSubmit={handleUploadDocUpdate}
                    isUpload={true}
                />
            )}
            {isLoading ? (
                <div
                    style={{
                        height: 300,
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        justifyContent: 'center',
                    }}
                >
                    <LoadingIndicator />
                </div>
            ) : (
                <>
                    <Grid>
                        <TextHeader style={{ textAlign: 'center', marginBottom: 10 }}>
                            Protocol Deviation / Compliance Incident Report Form
                        </TextHeader>
                    </Grid>
                    <Grid className={classes.itemContent}>
                        <TextSubHeader className={classes.textSubHeader}>1. Basic Information</TextSubHeader>

                        <SEvenRow style={{ marginTop: 10 }}>
                            <div>
                                <div>Scientific Title:</div>
                                <div>{remoteDetail?.scientificTitle}</div>
                            </div>
                        </SEvenRow>
                        <SEvenRow style={{ marginTop: 20 }}>
                            <div>
                                <div>IRB/REC Ref. No.:</div>
                                <div>{remoteDetail?.refNo}</div>
                            </div>
                            <div>
                                <div>Coordinating Investigator:</div>
                                <div>{remoteDetail?.ci}</div>
                            </div>
                        </SEvenRow>

                        <SEvenRow style={{ marginTop: 20 }}>
                            <div>
                                <div>Protocol No.:</div>
                                <div>{remoteDetail?.protocolNo}</div>
                            </div>
                            <div>
                                <div>Planned Sample Size (site):</div>
                                <div>{remoteDetail?.plannedSampleSize}</div>
                            </div>
                        </SEvenRow>

                        <SEvenRow style={{ marginTop: 20, alignItems: 'center' }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center' }}>Actual Study Start Date:</div>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{ width: 150 }}>
                                        <CustomDatePicker
                                            value={remoteDetail?.actualStudyStartDate}
                                            onChange={(date) => handleChange('actualStudyStartDate', new Date(date))}
                                            disabled={isViewOnly || isNotYetStarted}
                                            minDate={new Date(approvalDecisionDate).getTime()}
                                            maxDate={new Date().getTime()}
                                            validators={isNotYetStarted ? [] : ['isRequired']}
                                            forceErrorDisplay={forceErrorDisplay}
                                            isValidValue={(isValid) => setActualStudyStartDateValid(isValid)}
                                        />
                                    </div>
                                    <Checkbox
                                        size="small"
                                        color="primary"
                                        disabled={isViewOnly}
                                        onChange={changeNotYetStarted}
                                        checked={remoteDetail ? remoteDetail.isNotYetStarted : null}
                                    />
                                    Not yet started
                                </div>
                            </div>
                            <div>
                                <div>Anticipated Study End Date:</div>
                                <div>
                                    {remoteDetail?.antipicateStudyEndDate
                                        ? getTimeWithTimeZone(remoteDetail.antipicateStudyEndDate).format('YYYY-MM-DD')
                                        : '-'}
                                </div>
                            </div>
                        </SEvenRow>

                        <SEvenRow style={{ marginTop: 20 }}>
                            <div>
                                <div>Study Site of this deviation / incident:</div>
                                <div>
                                    <CustomTextField
                                        disabled={isViewOnly}
                                        label="Study Site of this deviation / incident"
                                        value={remoteDetail?.studySite}
                                        forceErrorDisplay={forceErrorDisplay}
                                        validators={['isRequired']}
                                        maxLength={255}
                                        isValidValue={(isValid, isMaxLengthValid) => {
                                            setStudySiteValid(isValid);
                                            updateMaxLength('studySite', isMaxLengthValid);
                                        }}
                                        onChange={(e) => {
                                            handleChange('studySite', e.target.value);
                                        }}
                                    />
                                </div>
                            </div>
                            <div>
                                <div>Site Principal Investigator of the above study site:</div>
                                <div>{remoteDetail?.piSiteName}</div>
                            </div>
                        </SEvenRow>
                    </Grid>
                    <ProtocolDeviationDetails
                        isPermission={!isViewOnly}
                        protocolDeviationReport={protocolDeviationReport}
                        handleChange={handleChange}
                        forceErrorDisplay={forceErrorDisplay}
                        updateMaxLength={updateMaxLength}
                    />
                    <Grid className={classes.itemContent}>
                        <TextSubHeader>Upload Supplementary Document</TextSubHeader>
                        {!isViewOnly && (
                            <CustomButton
                                disabled={isViewOnly}
                                style={{ marginTop: 8 }}
                                onClick={() => setShowUploadDocDialog(true)}
                            >
                                Add
                            </CustomButton>
                        )}
                        <Grid className={classes.subItem}>
                            <CustomTable
                                disabled={isViewOnly}
                                columns={uploadDocColumns}
                                rows={rows}
                                isDeleteAllow
                                onDelete={handleUploadDocDelete}
                            />
                        </Grid>
                    </Grid>
                </>
            )}
        </GeneralPostApprovalLayout>
    );
};

export default ProtocolDeviation;
